import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  FiHome,
  FiCheckSquare,
  FiAward,
  FiSettings,
  FiLogOut,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import AppBanner from "./AppBanner";

const MainLink = ({ icon, label, to, active, onClick }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(to);
    if (onClick) onClick(); // Close mobile menu
  };

  return (
    <button
      className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-3 mb-2
        ${active ? "bg-white bg-opacity-20 text-white" : "text-white text-opacity-90"}
        hover:bg-white hover:bg-opacity-20 hover:text-white transition-all duration-200`}
      onClick={handleClick}
    >
      <span
        className={`p-2 rounded-full ${active ? "bg-white text-orange-600" : "bg-white bg-opacity-20 text-white"}`}
      >
        {icon}
      </span>
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
};

const Layout = () => {
  const [opened, setOpened] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const handleLogout = () => {
    logout();
    navigate("/login");
    setOpened(false); // Close mobile menu
  };

  // Base navigation links available to all users
  const links = [
    { icon: <FiHome size={16} />, color: "blue", label: "Dashboard", to: "/" },
    {
      icon: <FiCheckSquare size={16} />,
      color: "teal",
      label: "Make Picks",
      to: "/picks",
    },
    {
      icon: <FiAward size={16} />,
      color: "yellow",
      label: "Leaderboard",
      to: "/leaderboard",
    },
  ];

  // Permission-based navigation links
  // Commissioners (and admins) can access League Management
  // Note: For now, we still use the global isCommissioner flag for backward compatibility
  // TODO: In the future, we'll check if user is commissioner of any league via API call
  const commissionerLinks = user?.isCommissioner
    ? [
        {
          icon: <FiUsers size={16} />,
          color: "green",
          label: "League Management",
          to: "/league-management",
        },
      ]
    : [];

  // Only admins can access Admin Panel
  const adminLinks = user?.isAdmin
    ? [
        {
          icon: <FiShield size={16} />,
          color: "red",
          label: "Admin Panel",
          to: "/admin",
        },
      ]
    : [];

  // Combine all available links based on user permissions
  const allLinks = [...links, ...commissionerLinks, ...adminLinks];

  // Mobile full-screen layout for picks only
  if (
    isMobile &&
    (location.pathname === "/" || location.pathname === "/picks")
  ) {
    return (
      // Fixed to the viewport (not min-h-screen) so `main` below is a real
      // remaining-space box — the picks deck fills it exactly instead of
      // overflowing the screen by the banner's height.
      <div
        className="flex flex-col safe-area overflow-hidden"
        // --app-height is the viewport actually visible under the mobile
        // browser's URL bar (see useAppHeight); 100dvh is the fallback.
        style={{ height: "var(--app-height, 100dvh)" }}
      >
        {/* Header */}
        <AppBanner
          showMenuButton={true}
          onMenuClick={() => setOpened(!opened)}
        />

        {/* Full Screen Content */}
        <main className="flex-1 min-h-0 overflow-hidden">
          <Outlet />
        </main>

        {/* Mobile menu overlay */}
        {opened && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setOpened(false)}
            />
            <div className="fixed left-0 w-64 h-full bg-gradient-to-b from-orange-400 to-orange-500 z-50 p-4">
              <div className="flex flex-col h-full overflow-auto">
                <div className="flex-grow">
                  {allLinks.map((link) => (
                    <MainLink
                      {...link}
                      key={link.label}
                      active={location.pathname === link.to}
                      onClick={() => setOpened(false)}
                    />
                  ))}
                </div>
                <div className="border-t border-white border-opacity-20 pt-4">
                  <MainLink
                    icon={<FiSettings size={16} />}
                    color="gray"
                    label="Account Settings"
                    to="/account"
                    active={location.pathname === "/account"}
                    onClick={() => setOpened(false)}
                  />
                  <button
                    className="w-full text-left px-3 py-2 rounded-md flex items-center gap-3 mb-2 text-white text-opacity-90 hover:bg-white hover:bg-opacity-20 hover:text-white transition-all duration-200"
                    onClick={handleLogout}
                  >
                    <span className="p-2 rounded-full bg-white bg-opacity-20 text-white">
                      <FiLogOut size={16} />
                    </span>
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Mobile layout for other pages
  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col safe-area">
        {/* Header */}
        <AppBanner
          showMenuButton={true}
          onMenuClick={() => setOpened(!opened)}
        />

        {/* Content with padding */}
        <main className="flex-1 overflow-auto bg-gray-50 p-4">
          <Outlet />
        </main>

        {/* Mobile menu overlay */}
        {opened && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setOpened(false)}
            />
            <div className="fixed left-0 w-64 h-full bg-gradient-to-b from-orange-400 to-orange-500 z-50 p-4">
              <div className="flex flex-col h-full overflow-auto">
                <div className="flex-grow">
                  {allLinks.map((link) => (
                    <MainLink
                      {...link}
                      key={link.label}
                      active={location.pathname === link.to}
                      onClick={() => setOpened(false)}
                    />
                  ))}
                </div>
                <div className="border-t border-white border-opacity-20 pt-4">
                  <MainLink
                    icon={<FiSettings size={16} />}
                    color="gray"
                    label="Account Settings"
                    to="/account"
                    active={location.pathname === "/account"}
                    onClick={() => setOpened(false)}
                  />
                  <button
                    className="w-full text-left px-3 py-2 rounded-md flex items-center gap-3 mb-2 text-white text-opacity-90 hover:bg-white hover:bg-opacity-20 hover:text-white transition-all duration-200"
                    onClick={handleLogout}
                  >
                    <span className="p-2 rounded-full bg-white bg-opacity-20 text-white">
                      <FiLogOut size={16} />
                    </span>
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="flex h-screen">
      {/* Mobile menu overlay */}
      {opened && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setOpened(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
        fixed md:static inset-y-0 left-0 z-30
        w-64 shadow-lg transform transition-transform duration-300 ease-in-out
        ${opened ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
      `}
        style={{
          background:
            "linear-gradient(180deg, #FF8200 0%, #FF9500 50%, #FFA500 100%)",
        }}
      >
        <div className="flex flex-col h-full">
          <div className="flex-grow p-4">
            {allLinks.map((link) => (
              <MainLink
                {...link}
                key={link.label}
                active={location.pathname === link.to}
              />
            ))}
          </div>

          <div className="p-4 border-t border-white border-opacity-20">
            <MainLink
              icon={<FiSettings size={16} />}
              color="gray"
              label="Account Settings"
              to="/account"
              active={location.pathname === "/account"}
            />
            <button
              className="w-full text-left px-3 py-2 rounded-md flex items-center gap-3 mb-2 text-white text-opacity-90 hover:bg-white hover:bg-opacity-20 hover:text-white transition-all duration-200"
              onClick={handleLogout}
            >
              <span className="p-2 rounded-full bg-white bg-opacity-20 text-white">
                <FiLogOut size={16} />
              </span>
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <AppBanner />

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50 p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
